import React, { useState, useEffect } from 'react';
import { Link, useLocation, useMatch } from 'react-router-dom';
import {
  Briefcase,
  Home,
  Info,
  FileText,
  FileCheck,
  Folder,
  PanelLeftClose,
  PanelLeftOpen,
  HelpCircle,
  EyeOff,
  Eye,
  Menu,
  X,
} from 'lucide-react';
import { Logo } from './Logo';
import { useRedactionMode } from '../contexts/RedactionModeContext';
import { UnifiedSidebar } from './UnifiedSidebar';
import { CreditWarningBanner } from './CreditWarningBanner';
import { ImpersonationBanner } from './ImpersonationBanner';
import { SafetyBanner } from './SafetyBanner';
import { CaseTabBar } from './CaseTabBar';
import { UserAvatarMenu } from './UserAvatarMenu';
import { useAskSidebar } from '../contexts/AskSidebarContext';
import { useIsDesktop } from '../hooks/useIsDesktop';


interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const isCaseDetail = !!useMatch('/cases/:caseId/*');
  const { isEnabled: redactionEnabled, toggle: toggleRedaction } = useRedactionMode();
  const { isMaximized } = useAskSidebar();
  const isDesktop = useIsDesktop();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem('sidebar-collapsed');
      return stored === null ? true : stored === 'true';
    } catch { return true; }
  });

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleCollapsed = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebar-collapsed', String(next)); } catch { /* noop */ }
      return next;
    });
  };

  // On mobile the sidebar is always "expanded" (full labels), collapse only applies on desktop
  const showCollapsed = isDesktop && isCollapsed;

  const isActive = (path: string, query?: string) => {
    if (!location.pathname.startsWith(path)) return false;
    if (query) return location.search === query;
    return true;
  };

  const navigationGroups = [
    {
      label: 'Main',
      items: [
        { path: '/home', label: 'Home', icon: Home, color: 'text-cyan-600' },
        { path: '/cases', label: 'Cases', icon: Folder, color: 'text-green-600' },
        { path: '/drafts', label: 'Drafts', icon: FileCheck, color: 'text-indigo-600' },
        { path: '/account', query: '?tab=credentials', label: 'Profile', icon: Briefcase, color: 'text-amber-600' },
        { path: '/templates', label: 'Templates', icon: FileText, color: 'text-purple-600' },
      ]
    },
    {
      label: 'Other',
      items: [
        { path: '/help', label: 'Help', icon: HelpCircle, color: 'text-teal-600' },
        { path: '/about', label: 'About', icon: Info, color: 'text-slate-500' },
      ]
    }
  ];

  return (
    <div className="flex h-screen bg-gradient-to-br from-medical-50 via-white to-primary-50 dark:from-medical-900 dark:via-medical-800 dark:to-medical-900">
      {/* Mobile top bar */}
      {!isDesktop && (
        <div className="fixed top-0 left-0 right-0 z-40 h-14 bg-white dark:bg-medical-800 border-b border-medical-100 dark:border-medical-700 flex items-center px-4 shadow-sm">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 -ml-2 rounded-lg text-medical-600 hover:bg-medical-50 dark:text-medical-300 dark:hover:bg-medical-700"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="ml-2">
            <Logo size="sm" showText />
          </div>
          <div className="ml-auto">
            <UserAvatarMenu />
          </div>
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {!isDesktop && mobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 transition-opacity"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar — overlay on mobile, static on desktop */}
      <div
        onClick={isDesktop && isCollapsed ? toggleCollapsed : undefined}
        className={`
          ${isDesktop
            ? `${isCollapsed ? 'w-16 cursor-pointer overflow-visible' : 'w-48 overflow-hidden'} relative`
            : `fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`
          }
          bg-white dark:bg-medical-800 shadow-lg border-r border-medical-100 dark:border-medical-700
          flex flex-col transition-all duration-300 flex-shrink-0
        `}
      >
        {/* Logo Section */}
        <div className={`py-4 pl-5 -ml-2 overflow-hidden border-b border-medical-100 dark:border-medical-700 transition-all duration-300 flex items-center`}>
          <Logo size="md" showText={!isCollapsed || !isDesktop} />
          {!isDesktop && (
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="ml-auto mr-4 p-1.5 rounded-lg text-medical-400 hover:bg-medical-50 hover:text-medical-600 dark:hover:bg-medical-700 dark:hover:text-medical-300"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className={`${showCollapsed ? 'overflow-visible' : 'overflow-y-auto scrollbar-hidden'} ${showCollapsed ? 'px-1.5' : 'px-2.5'} py-4`}>
          {navigationGroups.map((group, groupIndex) => (
            <div key={group.label} className={groupIndex > 0 ? 'mt-4' : ''}>
              {!showCollapsed && (
                <div className="px-3 mb-2">
                  <p className="text-sm font-semibold text-medical-400 dark:text-medical-300 uppercase tracking-wider">
                    {group.label}
                  </p>
                </div>
              )}
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path, item.query);
                  const linkTo = item.query ? item.path + item.query : item.path;
                  return (
                    <Link
                      key={linkTo}
                      to={linkTo}
                      onClick={showCollapsed ? (e: React.MouseEvent) => e.stopPropagation() : undefined}
                      className={`
                        group flex items-center ${showCollapsed ? 'justify-center px-2' : 'space-x-2.5 px-2.5'} py-2 rounded-lg text-base font-medium
                        transition-all duration-200 ease-in-out relative
                        ${active
                          ? 'bg-gradient-to-r from-primary-50 to-primary-100/50 dark:from-primary-900/30 dark:to-primary-800/30 text-primary-700 dark:text-primary-300 shadow-sm'
                          : 'text-medical-600 dark:text-medical-300 hover:bg-medical-50 dark:hover:bg-medical-700/50 hover:text-medical-900 dark:hover:text-medical-100'
                        }
                      `}
                    >
                      {/* Active indicator */}
                      {active && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary-600 rounded-r-full" />
                      )}

                      {/* Icon with color */}
                      <Icon
                        className={`h-4.5 w-4.5 transition-all duration-200 flex-shrink-0 ${
                          active
                            ? 'text-primary-600 scale-110'
                            : `${item.color} group-hover:scale-110`
                        }`}
                      />

                      {/* Label */}
                      {!showCollapsed && (
                        <span className={`truncate ${active ? 'font-semibold' : ''}`}>{item.label}</span>
                      )}

                      {/* Tooltip when collapsed (desktop only) */}
                      {showCollapsed && (
                        <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-medical-900 dark:bg-medical-700 text-white text-xs font-medium rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50 shadow-lg">
                          {item.label}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}

        </nav>

        {/* Bottom controls */}
        <div className={`flex-shrink-0 border-t border-medical-100 dark:border-medical-700 ${showCollapsed ? 'p-1.5' : 'px-2.5 py-2'} space-y-0.5`}>
          {/* Redaction Mode toggle */}
          <button
            onClick={(e) => { e.stopPropagation(); toggleRedaction(); }}
            className={`
              group w-full flex items-center ${showCollapsed ? 'justify-center px-2' : 'space-x-2.5 px-2.5'} py-2 rounded-lg text-sm font-medium
              transition-all duration-200 ease-in-out relative
              ${redactionEnabled
                ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30'
                : 'text-medical-400 dark:text-medical-500 hover:bg-medical-50 dark:hover:bg-medical-700/50 hover:text-medical-600 dark:hover:text-medical-300'
              }
            `}
            title={redactionEnabled ? 'Redaction Mode ON (\u2318\u21E7P)' : 'Redaction Mode OFF (\u2318\u21E7P)'}
          >
            {redactionEnabled
              ? <EyeOff className="h-4.5 w-4.5 transition-all duration-200 flex-shrink-0" />
              : <Eye className="h-4.5 w-4.5 transition-all duration-200 flex-shrink-0" />
            }
            {!showCollapsed && <span>{redactionEnabled ? 'Redaction On' : 'Redaction'}</span>}
            {showCollapsed && (
              <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-medical-900 dark:bg-medical-700 text-white text-xs font-medium rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50 shadow-lg">
                {redactionEnabled ? 'Redaction Mode ON' : 'Redaction Mode OFF'}
              </span>
            )}
          </button>

          {/* Collapse / Expand toggle — desktop only */}
          {isDesktop && (
            <button
              onClick={(e) => { e.stopPropagation(); toggleCollapsed(); }}
              className={`
                group w-full flex items-center ${showCollapsed ? 'justify-center px-2' : 'space-x-2.5 px-2.5'} py-2 rounded-lg text-sm font-medium
                transition-all duration-200 ease-in-out relative
                text-medical-400 dark:text-medical-500 hover:bg-medical-50 dark:hover:bg-medical-700/50 hover:text-medical-600 dark:hover:text-medical-300
              `}
              title={isCollapsed ? 'Expand menu' : 'Collapse menu'}
            >
              {isCollapsed
                ? <PanelLeftOpen className="h-4.5 w-4.5 transition-all duration-200 flex-shrink-0" />
                : <PanelLeftClose className="h-4.5 w-4.5 transition-all duration-200 flex-shrink-0" />
              }
              {!showCollapsed && <span>Collapse</span>}
              {showCollapsed && (
                <span className="absolute left-full ml-2 px-2.5 py-1.5 bg-medical-900 dark:bg-medical-700 text-white text-xs font-medium rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-50 shadow-lg">
                  Expand menu
                </span>
              )}
            </button>
          )}
        </div>

      </div>

      {/* Main Content + Document Panel */}
      <div className={`flex-1 flex overflow-hidden relative ${!isDesktop ? 'pt-14' : ''}`}>
        <div className="flex-1 flex flex-col overflow-hidden min-w-0 relative">
          <ImpersonationBanner />
          <SafetyBanner />
          <CreditWarningBanner />
          {isCaseDetail && <CaseTabBar />}
          <div className="flex-1 min-h-0 relative">
            <main
              className={`h-full pb-0 ${isMaximized ? 'overflow-hidden' : 'overflow-auto'}`}
              style={{ direction: 'rtl' }}
            >
              <div style={{ direction: 'ltr' }} className={isMaximized ? 'h-full' : undefined}>
                {/* User avatar — scrolls with page content (desktop only, mobile has it in top bar) */}
                {!isCaseDetail && isDesktop && (
                  <div className="sticky top-4 float-right mr-6 z-30">
                    <UserAvatarMenu />
                  </div>
                )}
                {children}
              </div>
            </main>
          </div>
        </div>

        {/* Unified three-tab sidebar: Documents, Assistant, Wizard (desktop only) */}
        {isDesktop && <UnifiedSidebar />}

      </div>

    </div>
  );
};
